import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Loader2,
  Pencil,
  PlugZap,
  RefreshCw,
  Search,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isIntegratedOrdersEnabled } from "@/features/integratedOrdersFeature";
import { useDebounce } from "@/hooks/useDebounce";
import {
  b2bRequestsApi,
  clientsApi,
  type IntegratedOrder,
  type IntegratedOrderQuery,
  type IntegratedOrderSortKey,
  type IntegratedOrderStatusFilter,
} from "@/lib/api";
import {
  formatIntegratedOrderDate,
  INTEGRATED_ORDER_SITUATION_LABELS,
  INTEGRATED_ORDER_STATUS_OPTIONS,
  INTEGRATION_TYPE_LABELS,
  integratedOrderActionLabel,
  integratedOrderErrorMessage,
  integratedOrderPageCount,
  integratedOrderSituation,
  routedCategoryLabel,
} from "@/lib/integratedOrders";
import { IntegratedOrderDetailsDialog } from "./IntegratedOrderDetailsDialog";
import { IntegratedOrderEditSheet } from "./IntegratedOrderEditSheet";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const ALL = "all";

interface Sort {
  key: IntegratedOrderSortKey;
  dir: "asc" | "desc";
}

// Colunas ordenáveis (a ordenação é feita no backend, por causa da paginação).
const SORTABLE_COLUMNS: { key: IntegratedOrderSortKey; label: string; className?: string }[] = [
  { key: "createdAt", label: "Recebido em" },
  { key: "client", label: "Escola" },
  { key: "product", label: "Produto" },
  { key: "cycleYear", label: "Ciclo" },
  { key: "quantity", label: "Qtd.", className: "justify-end" },
  { key: "integrationType", label: "Tipo" },
  { key: "preOrder", label: "Pré-pedido" },
  { key: "situation", label: "Situação" },
  { key: "action", label: "Ação" },
];

const situationVariant = (order: IntegratedOrder) => {
  const situation = integratedOrderSituation(order);
  return situation === "review" ? "destructive" : situation === "routed" ? "default" : "secondary";
};

export default function IntegratedOrders() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<IntegratedOrderStatusFilter>("all");
  const [cycleYear, setCycleYear] = useState<string>(ALL);
  const [integrationType, setIntegrationType] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<Sort>({ key: "createdAt", dir: "desc" });
  const [details, setDetails] = useState<IntegratedOrder | null>(null);
  const [editing, setEditing] = useState<IntegratedOrder | null>(null);
  const [toReintegrate, setToReintegrate] = useState<IntegratedOrder | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  // Qualquer mudança de filtro volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, cycleYear, integrationType, pageSize, sort]);

  const query = useMemo<IntegratedOrderQuery>(() => ({
    search: debouncedSearch,
    status,
    cycleYear: cycleYear === ALL ? null : Number(cycleYear),
    integrationType: integrationType === ALL ? null : Number(integrationType),
    sortBy: sort.key,
    sortDir: sort.dir,
    page,
    pageSize,
  }), [debouncedSearch, status, cycleYear, integrationType, sort, page, pageSize]);

  const { data: cycles = [] } = useQuery({
    queryKey: ["commercial-cycles-order"],
    queryFn: async () => (await clientsApi.getCycles()).cycles || [],
    staleTime: 5 * 60 * 1000,
    enabled: isIntegratedOrdersEnabled,
  });
  const cycleYears = useMemo(
    () => Array.from(new Set(cycles.map((cycle) => cycle.year))).sort((a, b) => b - a),
    [cycles],
  );

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["integrated-orders", "list", query],
    queryFn: () => b2bRequestsApi.list(query),
    placeholderData: keepPreviousData,
    enabled: isIntegratedOrdersEnabled,
  });
  const items = data?.items ?? [];
  const summary = data?.summary;
  const pageCount = integratedOrderPageCount(data?.total ?? 0, pageSize);

  // Primeiro clique ordena de forma crescente; o seguinte inverte. Datas começam pelas mais recentes.
  const toggleSort = (key: IntegratedOrderSortKey) =>
    setSort((current) => current.key === key
      ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "createdAt" ? "desc" : "asc" });

  const reintegrate = useMutation({
    mutationFn: (order: IntegratedOrder) => b2bRequestsApi.reintegrate(order.id),
    onSuccess: (result) => {
      if (result.needsReview) toast.warning(`Continua em revisão: ${result.reviewReason ?? result.message}`);
      else toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ["integrated-orders"] });
    },
    onError: (error) => toast.error(integratedOrderErrorMessage(error)),
    onSettled: () => setToReintegrate(null),
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await b2bRequestsApi.exportXlsx(query);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "pedidos-integrados.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(integratedOrderErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  };

  if (!isIntegratedOrdersEnabled) return <Navigate to="/admin-pedidos" replace />;

  const summaryCards: { key: IntegratedOrderStatusFilter; label: string; value?: number }[] = [
    { key: "all", label: "Total", value: summary?.total },
    { key: "review", label: "Em revisão", value: summary?.needsReview },
    { key: "valid", label: "Válidos", value: summary?.valid },
    { key: "routed", label: "Roteados ao programado", value: summary?.routed },
  ];

  return (
    <Layout title="Pedidos integrados">
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <PlugZap className="h-5 w-5" />
            <p>
              Itens recebidos do ERP (purchase_orders_b2b_request). Registros em revisão podem ser corrigidos e
              reintegrados pelo mesmo processo do ERP.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            <Button onClick={handleExport} disabled={isExporting || (data?.total ?? 0) === 0}>
              {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Exportar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const isSelected = status === card.key;
            return (
              <button
                key={card.key}
                type="button"
                onClick={() => setStatus(card.key)}
                className={`rounded-lg border-2 p-4 text-left transition-colors ${isSelected
                  ? "border-primary bg-primary/5"
                  : "border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-primary/5"}`}
              >
                <p className="text-xs text-muted-foreground">{card.label}</p>
                <p className="mt-1 text-2xl font-bold">{card.value ?? "—"}</p>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[280px] flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Buscar escola, CNPJ/CPF, SKU, produto, pré-pedido ou referência"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <Select value={status} onValueChange={(value) => setStatus(value as IntegratedOrderStatusFilter)}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INTEGRATED_ORDER_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cycleYear} onValueChange={setCycleYear}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os ciclos</SelectItem>
              {cycleYears.map((year) => <SelectItem key={year} value={String(year)}>Ciclo {year}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={integrationType} onValueChange={setIntegrationType}>
            <SelectTrigger className="w-[230px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os tipos</SelectItem>
              <SelectItem value="1">1 - {INTEGRATION_TYPE_LABELS[1]}</SelectItem>
              <SelectItem value="0">0 - {INTEGRATION_TYPE_LABELS[0]}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="flex items-center justify-between text-base">
              Registros do ERP
              <Badge variant="outline">{data?.total ?? 0} registros</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-3 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
            ) : isError ? (
              <div className="p-8 text-center text-sm text-destructive">Não foi possível consultar os pedidos integrados.</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Nenhum registro encontrado para os filtros.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {SORTABLE_COLUMNS.map(({ key, label, className }) => {
                      const active = sort.key === key;
                      const SortIcon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
                      return (
                        <TableHead key={key} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                          <button
                            type="button"
                            onClick={() => toggleSort(key)}
                            className={`inline-flex w-full items-center gap-1 font-medium hover:text-foreground ${className ?? ""}`}
                          >
                            {label}
                            <SortIcon className={`h-3.5 w-3.5 ${active ? "text-primary" : "opacity-50"}`} />
                          </button>
                        </TableHead>
                      );
                    })}
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="whitespace-nowrap text-sm">{formatIntegratedOrderDate(order.createdAt)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{order.clientName ?? "Escola não identificada"}</div>
                        <div className="text-xs text-muted-foreground">{order.clientCode ?? order.cnpjCpf ?? "—"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{order.productDescription ?? order.description ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">SKU {order.itemId ?? "—"}</div>
                      </TableCell>
                      <TableCell>{order.cycleYear ?? "—"}</TableCell>
                      <TableCell className="text-right">{order.quantity ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {order.integrationType != null ? order.integrationType : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{order.preOrderId ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{order.preOrderReference ?? ""}</div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant={situationVariant(order)}>
                            {INTEGRATED_ORDER_SITUATION_LABELS[integratedOrderSituation(order)]}
                          </Badge>
                          {order.needsReview && order.reviewReason && (
                            <div className="max-w-[280px] truncate text-xs text-destructive" title={order.reviewReason}>
                              {order.reviewReason}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.integrationAction ? (
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">{integratedOrderActionLabel(order.integrationAction)}</div>
                            {order.routedCategory && (
                              <div className="text-xs text-muted-foreground">
                                {routedCategoryLabel(order.routedCategory)}
                                {order.scheduledOrderNumber ? ` · ${order.scheduledOrderNumber}` : ""}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Ver detalhes" onClick={() => setDetails(order)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {order.canEdit && (
                            <>
                              <Button variant="ghost" size="icon" title="Corrigir" onClick={() => setEditing(order)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Reintegrar"
                                disabled={reintegrate.isPending}
                                onClick={() => setToReintegrate(order)}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                Itens por página
                <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                  <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>Página {page} de {pageCount}</span>
                <Button variant="outline" size="icon" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <IntegratedOrderDetailsDialog order={details} onClose={() => setDetails(null)} />
      <IntegratedOrderEditSheet order={editing} onClose={() => setEditing(null)} onSaved={() => refetch()} />

      <AlertDialog open={toReintegrate != null} onOpenChange={(open) => !open && !reintegrate.isPending && setToReintegrate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reintegrar registro {toReintegrate?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              O item do pré-pedido {toReintegrate?.preOrderId} será enviado de novo pelo mesmo processo do ERP, que
              resolve a escola e o produto pelo CNPJ/CPF e pelo SKU. Se passar, o registro deixa de estar em revisão
              (com roteamento ao pedido programado, quando for o caso, e recálculo da bonificação). Se continuar com
              problema, o motivo da revisão é atualizado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reintegrate.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={reintegrate.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (toReintegrate) reintegrate.mutate(toReintegrate);
              }}
            >
              {reintegrate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reintegrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
